import React from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import { closeError } from '../store/slices/globalErrorSlice';
import { t } from '../i18n';
import Dialog, { DialogActionButton } from './shared/Dialog';

/**
 * GlobalErrorDialog — catches all unhandled errors and offers recovery options.
 * Rendered at the root level to display globally across the entire app.
 *
 * Typically triggered by:
 * - Unhandled Promise rejections (ApiError, Error, network errors, etc.)
 * - Errors that weren't caught by component-level handlers
 */
const GlobalErrorDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const { message, statusCode } = useAppSelector((s) => s.globalError);

  const handleReload = (): void => {
    // Full page reload to clear any stale state and retry the operation
    window.location.reload();
  };

  const handleDismiss = (): void => {
    dispatch(closeError());
  };

  const actionButtons: DialogActionButton[] = [
    {
      label: t('error_reload_button'),
      onClick: handleReload,
      variant: 'btn-primary',
    },
  ];

  const errorTitle = statusCode ? `${t('error_title')} (${statusCode})` : t('error_title');

  const errorMessage = message || t('error_message');

  return (
    <Dialog
      title={errorTitle}
      onClose={handleDismiss}
      actionButtons={actionButtons}
      loading={false}
    >
      <p>{errorMessage}</p>
      <p className="error-dialog__hint">{t('error_reload_hint')}</p>
    </Dialog>
  );
};

export default GlobalErrorDialog;
